import { WebSocketMessage } from '../types/DeploymentRecord';
import { OktaAuth } from '@okta/okta-auth-js';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

const MAX_LOG_LEN = 500;

// Sanitize untrusted WebSocket payloads before logging:
// strip CR/LF/control chars to prevent log forging and cap length.
function sanitizeForLog(value: unknown): string {
  let s: string;
  try {
    s = typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    s = String(value);
  }
  // eslint-disable-next-line no-control-regex
  s = s.replace(/[\r\n\x00-\x1f\x7f\u2028\u2029]/g, ' ');
  return s.length > MAX_LOG_LEN ? `${s.slice(0, MAX_LOG_LEN)}…` : s;
}

interface WebSocketServiceConfig {
  url: string;
  oktaAuth?: OktaAuth;
  onMessage?: (message: WebSocketMessage) => void;
  onStatusChange?: (status: ConnectionStatus) => void;
  onError?: (error: Error) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketServiceConfig;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private messageQueue: any[] = [];
  private isConnecting = false;
  private manualDisconnect = false;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private firstPingTimer: NodeJS.Timeout | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private connectionStartTime: number = 0;
  private tokenRenewedHandler: ((key: string) => void) | null = null;
  // Set while closing for a token renewal so onclose reconnects at once without
  // counting it as a failed attempt.
  private renewing = false;

  constructor(config: WebSocketServiceConfig) {
    this.config = {
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      heartbeatInterval: 120000, // 2 minutes - balanced frequency to prevent timeout
      ...config
    };

    // Set up browser tab visibility handler
    this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  public async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    console.log('Attempting to connect to WebSocket:', this.config.url);
    this.isConnecting = true;
    this.manualDisconnect = false;
    this.subscribeToTokenRenewal();
    this.config.onStatusChange?.('connecting');

    try {
      // Get Okta access token if available
      let wsUrl = this.config.url;
      if (this.config.oktaAuth) {
        try {
          const accessToken = this.config.oktaAuth.getAccessToken();
          if (accessToken) {
            // Append token as query parameter for WebSocket authorization
            const separator = wsUrl.includes('?') ? '&' : '?';
            wsUrl = `${wsUrl}${separator}token=${accessToken}`;
          } else {
            console.warn('No Okta access token available');
          }
        } catch (error) {
          console.error('Error getting Okta access token:', error);
          this.config.onError?.(new Error('Failed to get authentication token'));
          this.isConnecting = false;
          this.config.onStatusChange?.('error');
          return;
        }
      }

      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        this.connectionStartTime = Date.now();
        console.log(`[${new Date().toISOString()}] WebSocket connected successfully to:`, this.config.url);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.config.onStatusChange?.('connected');

        // Start heartbeat to prevent idle timeout
        this.startHeartbeat();

        // Send getCurrent message on connection
        this.sendMessage({
          action: 'getCurrent'
        });

        // Send any queued messages
        while (this.messageQueue.length > 0) {
          const message = this.messageQueue.shift();
          this.sendMessage(message);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          // Log pong responses specifically for heartbeat debugging
          if (message.action === 'pong') {
            const elapsed = Math.round((Date.now() - this.connectionStartTime) / 1000);
            console.log(`[${new Date().toISOString()}] Received PONG response (${elapsed}s after connection)`, sanitizeForLog(message));
          } else {
            console.log('WebSocket message received:', sanitizeForLog(message));
          }

          this.config.onMessage?.(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, 'Raw data:', sanitizeForLog(event.data));
        }
      };

      this.ws.onerror = (error) => {
        // Log the type only: the Event's target.url carries the token.
        console.error('WebSocket error occurred:', error.type);
        console.error('WebSocket URL:', this.config.url);
        console.error('WebSocket ready state:', this.ws?.readyState);
        this.isConnecting = false;
        this.config.onStatusChange?.('error');
        this.config.onError?.(new Error(`WebSocket connection error: ${error.type}`));
      };

      this.ws.onclose = (event) => {
        const connectionDuration = this.connectionStartTime ? Math.round((Date.now() - this.connectionStartTime) / 1000) : 0;
        console.log(`[${new Date().toISOString()}] WebSocket disconnected after ${connectionDuration} seconds. Code: ${event.code}, Reason: ${event.reason}, Clean: ${event.wasClean}`);
        this.isConnecting = false;
        this.ws = null;
        this.config.onStatusChange?.('disconnected');

        // Stop heartbeat on disconnect
        this.stopHeartbeat();

        if (this.renewing) {
          this.renewing = false;
          if (!this.manualDisconnect) {
            console.log('Reconnecting with renewed access token');
            this.connect();
          }
          return;
        }

        // Log common WebSocket close codes for debugging
        const closeReasons: Record<number, string> = {
          1000: 'Normal Closure',
          1001: 'Going Away',
          1002: 'Protocol Error',
          1003: 'Unsupported Data',
          1004: 'Reserved',
          1005: 'No Status Received',
          1006: 'Abnormal Closure (often idle timeout)',
          1007: 'Invalid frame payload data',
          1008: 'Policy Violation',
          1009: 'Message too big',
          1010: 'Missing Extension',
          1011: 'Internal Error',
          1012: 'Service Restart',
          1013: 'Try Again Later',
          1014: 'Bad Gateway',
          1015: 'TLS Handshake'
        };

        console.log('Close code explanation:', closeReasons[event.code] || 'Unknown');

        // Only attempt to reconnect if it wasn't a manual disconnect
        if (!this.manualDisconnect && this.reconnectAttempts < (this.config.maxReconnectAttempts || 10)) {
          console.log(`Scheduling reconnect attempt ${this.reconnectAttempts + 1}`);
          this.scheduleReconnect();
        } else if (this.manualDisconnect) {
          console.log('Manual disconnect - not attempting to reconnect');
        } else {
          console.log('Max reconnect attempts reached');
        }
      };
    } catch (error) {
      // The constructor's error message can echo the URL, which carries the token.
      console.error('Error creating WebSocket:', (error as Error)?.name ?? 'unknown error');
      this.isConnecting = false;
      this.config.onStatusChange?.('error');
      this.config.onError?.(new Error('Failed to create WebSocket connection'));
    }
  }

  // The server fixes a connection's groups at $connect, so reopen the socket on
  // each access-token renewal to keep its claims no older than the token.
  private subscribeToTokenRenewal(): void {
    if (!this.config.oktaAuth || this.tokenRenewedHandler) return;
    this.tokenRenewedHandler = (key: string) => {
      if (key === 'accessToken') this.handleAccessTokenRenewed();
    };
    this.config.oktaAuth.tokenManager.on('renewed', this.tokenRenewedHandler);
  }

  private unsubscribeFromTokenRenewal(): void {
    if (this.config.oktaAuth && this.tokenRenewedHandler) {
      this.config.oktaAuth.tokenManager.off('renewed', this.tokenRenewedHandler);
    }
    this.tokenRenewedHandler = null;
  }

  private handleAccessTokenRenewed(): void {
    if (this.manualDisconnect) return;
    // No socket means a reconnect is already pending; it reads the new token.
    if (!this.ws || this.ws.readyState === WebSocket.CLOSING || this.ws.readyState === WebSocket.CLOSED) return;
    console.log('Access token renewed - reopening WebSocket');
    this.renewing = true;
    this.ws.close(1000, 'Access token renewed');
  }

  private getReconnectDelay(): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const baseDelay = 1000;
    const maxDelay = 30000;
    const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts), maxDelay);

    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 1000;
    return delay + jitter;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = this.getReconnectDelay();
    console.log(`Scheduling reconnect in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      console.log(`Reconnect attempt ${this.reconnectAttempts}`);
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing heartbeat

    console.log(`[${new Date().toISOString()}] Starting heartbeat: first ping in 25s, then every ${this.config.heartbeatInterval}ms`);

    // Send first ping after 25 seconds (before 32-second timeout)
    this.firstPingTimer = setTimeout(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const elapsed = Math.round((Date.now() - this.connectionStartTime) / 1000);
        console.log(`[${new Date().toISOString()}] Sending FIRST heartbeat ping (${elapsed}s after connection)`);
        this.sendMessage({ action: 'ping' });
      }
    }, 25000);

    // Then continue pinging every 90 seconds
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const elapsed = Math.round((Date.now() - this.connectionStartTime) / 1000);
        console.log(`[${new Date().toISOString()}] Sending periodic heartbeat ping (${elapsed}s after connection)`);
        this.sendMessage({ action: 'ping' });
      }
    }, this.config.heartbeatInterval!);
  }

  private stopHeartbeat(): void {
    if (this.firstPingTimer) {
      console.log(`[${new Date().toISOString()}] Stopping first ping timer`);
      clearTimeout(this.firstPingTimer);
      this.firstPingTimer = null;
    }
    if (this.heartbeatInterval) {
      console.log(`[${new Date().toISOString()}] Stopping periodic heartbeat`);
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      console.log('Tab hidden - maintaining connection with heartbeat');
      // Connection stays active with heartbeat
    } else {
      console.log('Tab visible - checking connection health');
      // Verify connection is still alive
      if (this.ws?.readyState === WebSocket.CLOSED || this.ws?.readyState === WebSocket.CLOSING) {
        console.log('Connection lost while hidden, reconnecting...');
        this.connect();
      }
    }
  }

  public disconnect(): void {
    console.log('Manual disconnect requested');
    this.manualDisconnect = true;

    // Stop heartbeat
    this.stopHeartbeat();

    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.unsubscribeFromTokenRenewal();
    this.renewing = false;

    // Remove visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }

    // Close WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.reconnectAttempts = 0;
    this.config.onStatusChange?.('disconnected');
  }

  public sendMessage(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Queue message if not connected
      this.messageQueue.push(message);
      console.log('Message queued, WebSocket not connected');
    }
  }

  public getStatus(): ConnectionStatus {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'disconnected';
    }
  }

  public isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}